import * as React from "react";
import { ThemeProvider } from "styled-components";
import Body from "@/components/core-blocks/Body";
import CookieConsent from "@/components/core-blocks/CookieConsent";
import Footer from "@/components/core-blocks/Footer";
import Header from "@/components/core-blocks/Header";
import Main from "@/components/core-blocks/Main";
import Seo, { SEOProps } from "@/components/core-blocks/Seo";
import Flexible, { IFlexible } from "@/src/components/core-blocks/Flexible";
import GlobalStyle, { MainInner } from "@/styles/global";
import defaultTheme from "@/styles/themes/default.theme";
import GtmContainer from "@/components/core-blocks/GtmContainer";
import {
  ColorOptions,
  IGlobalComponents,
  IWPImage,
  IWPRelatedPosts,
} from "@/src/types";
import {
  findAndStoreUtmTags,
  storeReferrer,
  gatsbyImageHelper,
  isLight,
} from "@/src/helpers";
import S from "./blog.styled";
import { Wysiwyg } from "@/styles/components";
import RelatedPosts from "@/components/global-components/RelatedPosts";

export type IPageTemplate = {
  pageContext: {
    flexible: IFlexible;
    globalComponents: IGlobalComponents;
    seo: SEOProps;
    title: string;
    date: string;
    blogInfo: {
      featuredImage: IWPImage;
      contentBgColor: ColorOptions;
      content: string;
      relatedPosts: IWPRelatedPosts[];
      author: {
        staffInfo: {
          firstName: string;
          lastName: string;
        };
      };
    };
  };
};

const BlogTemplate: React.FC<IPageTemplate> = ({
  pageContext: {
    flexible,
    globalComponents,
    title,
    date,
    blogInfo: { featuredImage, content, contentBgColor, relatedPosts, author },
  },
}) => {
  contentBgColor = contentBgColor || "white";

  const featuredImageData = gatsbyImageHelper(featuredImage);

  //find and store utm tags in local storage
  React.useEffect(() => {
    findAndStoreUtmTags();
    storeReferrer();
  }, []);

  return (
    <ThemeProvider theme={defaultTheme}>
      <GlobalStyle />
      <Body>
        <Header />
        <Main>
          <S.BlogSection>
            <MainInner>
              <S.BlogInner>
                {title && <S.Title>{title}</S.Title>}
                <S.InfoWrapper>
                  {author?.staffInfo && (
                    <S.Date>
                      By {author?.staffInfo.firstName}{" "}
                      {author?.staffInfo.lastName}
                    </S.Date>
                  )}
                  {date && <S.Date>PUBLISHED {date}</S.Date>}
                </S.InfoWrapper>

                {featuredImageData && (
                  <S.Image
                    image={featuredImageData}
                    alt={featuredImage.altText}
                  />
                )}
              </S.BlogInner>
            </MainInner>
            <S.ContentWrapper $bgColor={contentBgColor}>
              <MainInner>
                {content && (
                  <Wysiwyg
                    html={content}
                    maxWidth={802}
                    color={
                      contentBgColor === "yellow"
                        ? "brown"
                        : isLight(contentBgColor)
                          ? "black"
                          : "creme"
                    }
                  />
                )}
              </MainInner>
            </S.ContentWrapper>
          </S.BlogSection>
          <Flexible
            components={flexible.components}
            globalComponents={globalComponents}
          />
          {relatedPosts && <RelatedPosts relatedPosts={relatedPosts} />}
          <Footer />
          <CookieConsent />
        </Main>
      </Body>
    </ThemeProvider>
  );
};

export default BlogTemplate;

export function Head({ pageContext }: IPageTemplate) {
  return (
    <>
      {/* <GtmContainer /> */}
      <html lang="en" />
      <Seo {...pageContext.seo} lang="en" />
    </>
  );
}
